import { createValidationIssue } from "../issues.js";

function normalizeSelectedFileCount(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : 0;
}

function buildImportUiIssue({
  code,
  field = "request",
  message,
  metadata = null,
  severity = "error",
} = {}) {
  return createValidationIssue({
    code,
    field,
    message,
    metadata,
    scope: "import",
    severity,
    source: "ui",
  });
}

function buildRequestErrorResult(message) {
  return {
    field: "request",
    filename: "",
    message,
    status: "error",
  };
}

function buildImportClientFeedbackState({
  code,
  field = "request",
  issueMessage,
  metadata = null,
  resultMessage,
  severity = "error",
  tone = "danger",
} = {}) {
  const issue = buildImportUiIssue({
    code,
    field,
    message: issueMessage,
    metadata,
    severity,
  });

  return {
    feedback: {
      issue,
      message: issue.message,
      tone,
    },
    results: [buildRequestErrorResult(resultMessage)],
  };
}

export function hasSelectedImportFiles(selectedFileCount) {
  return normalizeSelectedFileCount(selectedFileCount) > 0;
}

export function isImportActionDisabled({ isSubmitting, selectedFileCount }) {
  return Boolean(isSubmitting) || !hasSelectedImportFiles(selectedFileCount);
}

export function canOpenImportConfirmation({ isSubmitting, selectedFileCount }) {
  return !isImportActionDisabled({ isSubmitting, selectedFileCount });
}

export function resolveImportSubmissionValidationState({ selectedFileCount = 0 } = {}) {
  if (hasSelectedImportFiles(selectedFileCount)) {
    return null;
  }

  return buildImportSelectionRequiredFeedback({ selectedFileCount });
}

export function resolveImportSessionAccessState({ error = null, session = null } = {}) {
  const accessToken = typeof session?.access_token === "string"
    ? session.access_token.trim()
    : "";

  if (!error && accessToken) {
    return {
      accessToken,
      state: null,
    };
  }

  return {
    accessToken: "",
    state: buildImportSessionUnavailableFeedback(),
  };
}

export function buildImportSelectionRequiredFeedback({ selectedFileCount = 0 } = {}) {
  return buildImportClientFeedbackState({
    code: "import.selection_required",
    issueMessage: "Sélectionnez au moins un fichier Excel avant l’envoi.",
    metadata: {
      selectionCount: normalizeSelectedFileCount(selectedFileCount),
    },
    resultMessage: "Aucun fichier Excel n’a été sélectionné.",
  });
}

export function buildImportSessionUnavailableFeedback() {
  return buildImportClientFeedbackState({
    code: "import.session_unavailable",
    issueMessage: "Votre session Supabase n’est plus disponible. Reconnectez-vous avant de relancer l’import.",
    metadata: {
      reason: "missing_access_token",
    },
    resultMessage: "Aucun jeton d’accès valide n’a été trouvé dans la session courante.",
  });
}

export function buildImportNetworkFailureFeedback(error) {
  return buildImportClientFeedbackState({
    code: "import.network_error",
    issueMessage: "Une erreur réseau a interrompu l’envoi. Réessayez après avoir vérifié la disponibilité du backend.",
    metadata: error instanceof Error && error.message
      ? {
        errorMessage: error.message,
      }
      : null,
    resultMessage: error instanceof Error && error.message
      ? error.message
      : "Erreur inattendue pendant l’import.",
  });
}